import { StatsigAutoCapturePlugin } from '@statsig/web-analytics'
import { StatsigSessionReplayPlugin } from '@statsig/session-replay'
import { LocalOverrideAdapter } from '@statsig/js-local-overrides'
import { LogLevel, StatsigClient } from '@statsig/js-client'
import {
  hasConsentForServices,
  initializePluginWithConsent,
  ServiceIds,
} from 'chimera/all/plugins/config/usercentrics.client'
import { EventBus } from 'chimera/all/plugins/eventbus'
import { bindEvents } from './bind'
import { bindDevLogging } from './devLogger'

let statsigClient = null
let initPromise = null
let hasConsent = false

/**
 * Get the Statsig client instance
 * @returns {object|null}
 */
export const getStatsigClient = () => statsigClient

/**
 * Get the initialization promise
 * @returns {Promise|null}
 */
export const getInitPromise = () => initPromise

/**
 * Enable logging and storage after consent is given
 * This should be called when the user gives consent for tracking
 */
export const enableAfterConsent = async () => {
  if (!statsigClient || hasConsent) {
    return
  }

  // Wait for SDK to be fully initialized before enabling logging
  if (initPromise) {
    await initPromise
  }

  hasConsent = true
  statsigClient.updateRuntimeOptions({
    loggingEnabled: 'browser-only',
    disableStorage: false,
  })

  // Flush any queued events
  await statsigClient.flush()
}

/**
 * Initialize Statsig plugin
 * @param {object} Vue
 * @param {object} options
 * @param {string} options.clientKey
 * @param {string} options.visitorId
 * @param {string} options.sessionId
 * @param {string} options.environment
 * @param {string} options.deviceType
 * @param {string} options.country
 * @param {string} options.language
 * @param {string} options.label - Concept/application name
 */
export default (
  Vue,
  {
    clientKey,
    visitorId,
    sessionId,
    environment,
    deviceType,
    country,
    language,
    label,
  },
) => {
  const tier = environment || 'local'

  statsigClient = new StatsigClient(
    clientKey,
    {
      customIDs: {
        'Visitor ID': visitorId,
        'Session ID': sessionId,
      },
      custom: {
        deviceType,
        country,
        language,
        label,
      },
    },
    {
      environment: { tier },
      overrideAdapter: new LocalOverrideAdapter(),
      logLevel: LogLevel.None,

      /**
       * GDPR compliance: Start with logging disabled.
       * The SDK buffers up to 500 events in memory and flushes them once logging is re-enabled.
       * @see https://docs.statsig.com/client/javascript-sdk#how-do-i-handle-consent-or-gdpr-flows
       */
      loggingEnabled: 'disabled',
      disableStorage: true,

      /**
       * Plugins for automatic event capture and session replay
       * @see https://docs.statsig.com/webanalytics/overview
       */
      plugins: [
        new StatsigAutoCapturePlugin(),
        new StatsigSessionReplayPlugin(),
      ],
    },
  )

  initPromise = statsigClient.initializeAsync()
  initPromise
    .catch((error) => {
      EventBus.emitErrorAppErrorEvent(
        new Error('Failed to initialize Statsig'),
        { error, context: 'statsig/install' },
      )
    })
    .finally(() => {
      window.dispatchEvent(new CustomEvent('experimentation:ready'))
    })

  // Flush events when user leaves the page to prevent data loss
  window.addEventListener('beforeunload', () => {
    if (hasConsent) {
      statsigClient.shutdown()
    }
  })

  bindEvents(Vue, { statsigClient })

  // Attach dev-console logging listeners before emitting any events, so early
  // emits (like session_start below) are visible in the dev console.
  if (tier !== 'production') {
    bindDevLogging(statsigClient)
  }

  // Emit session start first, before any other tracking events.
  // Statsig buffers events (up to 500) while logging is disabled and flushes on consent.
  EventBus.emitSessionStartEvent()

  const requiredServiceIds = [ServiceIds.STATSIG]

  // Check for existing consent on page load (user already consented in previous session)
  // Wait for initialization before checking consent to avoid race conditions
  initPromise.then(async () => {
    const consentGiven = await hasConsentForServices(requiredServiceIds)
    if (consentGiven) {
      await enableAfterConsent()
    }
  })

  // Listen for future consent events (user interacts with consent banner)
  initializePluginWithConsent(async () => {
    await enableAfterConsent()
  }, requiredServiceIds)
}
